import { inject } from '@angular/core';
import { IFBWindow } from '@app/models/window-namespace';
import { AccountResource } from '@shared/modules/resources/account.resource';
import { ColorThemeResource } from '@shared/modules/resources/color-theme.resource';
import { CompanyResource } from '@shared/modules/resources/company.resource';
import { FeatureTogglesResource } from '@shared/modules/resources/feature-toggles.resouce';
import { ColorThemeService } from '@shared/services/color-theme/color-theme.service';
import { CompanyService } from '@shared/services/company.service';
import { LanguagesService } from '@shared/services/languages.service';
import { SsoService } from '@shared/services/sso.service';
import { UserManager } from '@shared/services/user-manager.service';
import browserHelper from '@utils/browser-helper';
import { featureFlags } from '@utils/feature-flags';
import locationHelper from '@utils/location-helper';
import { logger } from '@utils/logger.service';
import { tap, catchError, of, forkJoin, switchMap } from 'rxjs';
import { storage } from '@utils/storage.service';
import { logErrorAndReturnNull, ObservableRequestMap } from './observable-request-helper';

export function initializePublicApp() {
  const accountResource = inject(AccountResource);
  const colorThemeResource = inject(ColorThemeResource);
  const colorThemeService = inject(ColorThemeService);
  const companyResource = inject(CompanyResource);
  const companyService = inject(CompanyService);

  const featureTogglesResource = inject(FeatureTogglesResource);
  const languagesService = inject(LanguagesService);
  const ssoService = inject(SsoService);
  const userManager = inject(UserManager);
  const window = inject(IFBWindow);

  const fetchData = () => {
    const token = storage.getItem('token') || locationHelper.hashParam('access_token');
    const requests: ObservableRequestMap = {};

    const subdomain = companyService.subdomain;
    if (subdomain) {
      requests.company = companyResource.getCompany(subdomain).pipe(
        tap(company => {
          companyService.set(company);
        })
      );
    } else {
      requests.languages = languagesService.getAllowed().pipe(
        tap(languages => companyService.setEnabledLocales(languages.map(language => language.locale))),
        logErrorAndReturnNull()
      );
    }

    if (token) {
      requests.account = accountResource.get(token).pipe(
        tap(account => userManager.set(account)),
        logErrorAndReturnNull()
      );
    }

    requests.features = featureTogglesResource.get().pipe(
      tap(response => featureFlags.set(response)),
      logErrorAndReturnNull()
    );

    return forkJoin(requests).pipe(
      catchError(err => {
        if (token) {
          storage.removeItem('token');
        }
        logger.error(err);
        return of(null);
      }),
      switchMap(() => {
        if (companyService.data?.id) {
          const companyRequests: ObservableRequestMap = {};

          companyRequests.colorTheme = colorThemeResource.get().pipe(
            tap(theme => colorThemeService.applyTheme(theme)),
            catchError(err => {
              colorThemeService.applyTheme();
              logger.error(err);
              return of(null);
            })
          );

          companyRequests.companyFeatures = companyResource.getCompanyFeaturesConf().pipe(
            tap(companyFeatures => featureFlags.set(companyFeatures)),
            logErrorAndReturnNull()
          );

          companyRequests.languages = companyResource.getCompanyLanguages().pipe(
            tap(languages => companyService.setLanguages(languages)),
            logErrorAndReturnNull()
          );

          companyRequests.metadata = companyResource.getMetadataConf().pipe(
            tap(metadata => featureFlags.set(metadata.components)),
            logErrorAndReturnNull()
          );

          companyRequests.ssoOnly = ssoService.fetchIdentityProviders().pipe(logErrorAndReturnNull());

          return forkJoin(companyRequests);
        } else {
          colorThemeService.init();
        }

        return of(null);
      })
    );
  };

  if (browserHelper.isCname()) {
    return companyResource.getMetadata().pipe(
      switchMap(metaData => {
        window.localStorage.setItem('companySubdomain', metaData.domainName);
        return fetchData();
      })
    );
  } else {
    return fetchData();
  }
}
